import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import type { CartItem } from '../types';
import { getCartItemUnitPrice } from '../utils/cartPrice';

/**
 * Riga del carrello come la scriverà il gestionale sull'ordine
 * (`POST /api/prices/cart`, vedi `erp/pricing.py::quote_righe`): il
 * gestionale comanda, carrello e checkout mostrano e inviano questi valori.
 *
 * `prezzo` è il listino: gli sconti li applica NTS, e un prezzo già netto
 * verrebbe scontato due volte. Lo sconto scatola segue il portale: solo le
 * scatole intere sono scontate, i pezzi sciolti no (`scatola`); il backend
 * lo manda a NTS come una riga a prezzo scontato.
 */
export interface RigaCarrello {
  sku: string;
  qty: number;
  prezzo: number;
  sconti: number[];
  percentualeSconto: number;
  totaleLinea: number;
  source: string;
  /** Presente quando la riga ha almeno una scatola intera. */
  scatola?: { pezziPerScatola: number; scatole: number; sconto: number; risparmio: number } | null;
}

interface CartQuoteResponse {
  righe: RigaCarrello[];
  imponibile: number;
  /** Non vendibili online su NTS: non hanno prezzo e non si ordinano. */
  nonVendibili?: string[];
  /** Articoli rimossi dal gestionale: come i non vendibili, con il loro messaggio. */
  rimossi?: string[];
}

export interface CartQuote {
  /** Riga di un articolo; senza risposta dal backend, il listino senza sconti.
   *  `null` per un articolo non vendibile. */
  riga: (item: CartItem) => RigaCarrello | null;
  /** L'articolo non si può ordinare: non vendibile online (`webSellable`
   *  falso su NTS) o rimosso dal gestionale. */
  nonVendibile: (item: CartItem) => boolean;
  /** L'articolo è stato rimosso dal gestionale: "Articolo non più disponibile". */
  rimosso: (item: CartItem) => boolean;
  /** Almeno un articolo del carrello non è vendibile: l'ordine non parte. */
  haNonVendibili: boolean;
  imponibile: number;
  /** Somma a prezzo di listino, prima degli sconti. */
  totaleListino: number;
  loading: boolean;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

function rigaListino(item: CartItem): RigaCarrello | null {
  const prezzo = getCartItemUnitPrice(item);
  if (prezzo <= 0) return null;
  return {
    sku: item.sku, qty: item.qty, prezzo, sconti: [], percentualeSconto: 0,
    totaleLinea: round2(prezzo * item.qty), source: 'cart',
  };
}

export function useCartQuote(items: CartItem[], enabled: boolean): CartQuote {
  const key = items.map(i => `${i.sku}:${i.qty}`).join('|');
  const [quote, setQuote] = useState<{
    key: string; bySku: Record<string, RigaCarrello>; nonVendibili: string[]; rimossi: string[];
  } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || items.length === 0) return;
    let cancelled = false;
    // Attesa breve: chi cambia la quantità a colpi di "+" fa una chiamata sola.
    const t = setTimeout(() => {
      setLoading(true);
      api.post<CartQuoteResponse>('/api/prices/cart', { items: items.map(i => ({ sku: i.sku, qty: i.qty })) })
        .then(({ data }) => {
          if (cancelled) return;
          setQuote({
            key, bySku: Object.fromEntries(data.righe.map(r => [r.sku, r])),
            nonVendibili: data.nonVendibili ?? [], rimossi: data.rimossi ?? [],
          });
        })
        .catch(() => { /* resta la stima a listino; il backend corregge alla conferma (409) */ })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return useMemo(() => {
    const fresh = quote && quote.key === key ? quote.bySku : null;
    // Anche il flag del prodotto al momento dell'aggiunta, finché il backend
    // non ha risposto.
    const bloccati = new Set(quote?.nonVendibili ?? []);
    const tolti = new Set(quote?.rimossi ?? []);
    const rimosso = (item: CartItem) => tolti.has(item.sku) || item.rimossoDalGestionale === true;
    const nonVendibile = (item: CartItem) => rimosso(item) || bloccati.has(item.sku) || item.webSellable === false;
    const riga = (item: CartItem) => (nonVendibile(item) ? null : fresh?.[item.sku] ?? rigaListino(item));
    let imponibile = 0;
    let totaleListino = 0;
    for (const item of items) {
      const r = riga(item);
      if (!r) continue;
      imponibile += r.totaleLinea;
      totaleListino += r.prezzo * item.qty;
    }
    return {
      riga,
      nonVendibile,
      rimosso,
      haNonVendibili: items.some(nonVendibile),
      imponibile: round2(imponibile),
      totaleListino: round2(totaleListino),
      loading: enabled && items.length > 0 && (loading || !fresh),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, key, loading, enabled]);
}
